// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * The hint above the documents list (see document-hints.ts), and its setting in Settings ›
 * Appearance: the Show hints checkbox, with Take the tour beside it.
 */
import { hostHas } from '../shared/host-capabilities';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, Gauge, Play, Plane, Sparkles, X } from 'lucide-react';
import type { ProcessingMode, ProcessResult, ReviewOutcomes } from '../shared/desktop-api';
import {
  HINTS_EVENT,
  HINT_WORDS,
  closeHint,
  firstReviewDone,
  hintToShow,
  readHintSettings,
  setShowHints,
  showHintsChecked,
  waitingDocumentIds,
  type HintId,
  type HintSettingsState,
  type SpeedTestFacts,
} from './document-hints';
import {
  TOUR_OFFER_KEY,
  TOUR_TAKEN_KEY,
  readTourFlag,
  shouldOfferOwnTour,
  tourOfferLabel,
} from './tour';
import { useTour } from './TourCard';
import { MILESTONE_EVENT, hasMilestone } from './onboarding';
import { OPEN_SETUP_SECTION_EVENT } from './FirstReviewGuide';
import { pilotMode, useAiReviewAvailable } from './Autopilot';
import { SETUP_TIPS } from './setup-tips';
import { hintTelemetry } from './hint-telemetry';
import './tour.css';

/** The setup screen's Let it run and AI check tips: those two hints say the same (owner, October 4). */
const LET_IT_RUN = SETUP_TIPS.find((tip) => tip.id === 'autopilot');
const SECOND_LOOK = SETUP_TIPS.find((tip) => tip.id === 'ai-review');

/** Asks the app to start the sample tour (Settings' Take the tour). */
export const TAKE_TOUR_EVENT = 'capturepoint-take-tour';
/** The speed test hint's Run speed test: the app opens This computer › Performance, which starts the test. */
export const RUN_SPEED_TEST_EVENT = 'capturepoint-run-speed-test';

const bold = (text: string) =>
  text.split(/\*\*(.+?)\*\*/).map((part, index) => (index % 2 ? <b key={index}>{part}</b> : part));

/** Show hints and the hints closed or retired, re-read whenever that changes (here or in another window). */
function useHintSettings(): HintSettingsState {
  const [state, setState] = useState(() => readHintSettings());
  useEffect(() => {
    const update = () => setState(readHintSettings());
    window.addEventListener(HINTS_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(HINTS_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return state;
}

function useMilestoneFacts(): { known: boolean; exported: boolean } {
  const read = () => ({
    known: hasMilestone('firstLaunch'),
    exported: hasMilestone('firstExport'),
  });
  const [state, setState] = useState(read);
  useEffect(() => {
    const update = () => setState(read());
    update();
    window.addEventListener(MILESTONE_EVENT, update);
    return () => window.removeEventListener(MILESTONE_EVENT, update);
  }, []);
  return state;
}

type JobReviewFacts = { review: ReviewOutcomes | null; sendsByItself: boolean };
const REVIEW_FACTS_INTERVAL_MS = 30_000;
/** One look per job, shared by every hint showing it (the review list's and the caught-up rail's each asked on their own). */
const reviewFactLooks = new Map<string, { at: number; look: Promise<JobReviewFacts> }>();
function lookAtJobReviewFacts(jobId: string): Promise<JobReviewFacts> {
  const recent = reviewFactLooks.get(jobId);
  // A look still running, or one answered within the interval, is the answer.
  if (recent && Date.now() - recent.at < REVIEW_FACTS_INTERVAL_MS - 1_000) return recent.look;
  const api = window.capturePointDesktop;
  if (!api) return Promise.reject(new Error('The desktop bridge is not ready.'));
  // PAIGE: whether it sends by itself is the host's answer (sendsByItselfNow): Paige keeps no export settings.
  const look = Promise.all([api.getReviewOutcomes(jobId), sendsByItselfNow(jobId)]).then(
    ([review, sendsByItself]) => ({ review, sendsByItself }),
  );
  const entry = { at: Date.now(), look };
  reviewFactLooks.set(jobId, entry);
  // Measured from the answer, not the ask: a slow answer does not let the next look start at once.
  void look.then(
    () => {
      entry.at = Date.now();
    },
    () => {
      if (reviewFactLooks.get(jobId) === entry) reviewFactLooks.delete(jobId);
    },
  );
  return look;
}

/** The engine's review outcomes for this job, and whether it sends by itself; re-read now and then. */
function useJobReviewFacts(jobId: string, wanted: boolean): JobReviewFacts {
  const [state, setState] = useState<JobReviewFacts>({ review: null, sendsByItself: false });
  useEffect(() => {
    // Hints off: nothing here is shown, so nothing is asked. Each look also waits for the one before it, and every
    // hint on screen shares it: on a job with a long history the engine took a minute to answer, and two hints each
    // looking every half minute kept both of its workers busy for good, so every other call queued behind them for
    // minutes (GDE, 2026-11-02).
    if (!wanted) return;
    let live = true;
    let reading = false;
    const read = async () => {
      if (reading) return;
      reading = true;
      try {
        const facts = await lookAtJobReviewFacts(jobId);
        if (live) setState(facts);
      } catch {
        /* The next look tries again. */
      } finally {
        reading = false;
      }
    };
    void read();
    const timer = window.setInterval(() => void read(), REVIEW_FACTS_INTERVAL_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [jobId, wanted]);
  return state;
}

/**
 * Whether the job exports as soon as documents are ready. PAIGE: Paige always sends as soon as
 * ready, to every destination besides Download, so the question is whether there is one
 * (host-export.ts); the desktop asks its own export settings.
 */
async function sendsByItselfNow(jobId: string): Promise<boolean> {
  if (!hostHas('desktop-export')) {
    const facts = await window.paigeWorkbench?.exportFacts?.();
    return (facts?.destinations ?? 0) > 0;
  }
  const settings = await window.capturePointDesktop?.getExportSettings(jobId);
  return Boolean(settings?.enabled && settings.triggerMode === 'continuous');
}

/**
 * Whether a speed test has ever finished on this computer (a saved test result), and whether one
 * runs now; null until read. A test that finishes while this is shown counts at once.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function useSpeedTestFacts(): SpeedTestFacts | null {
  const [facts, setFacts] = useState<{ finished: boolean | null; running: boolean }>({
    finished: null,
    running: false,
  });
  useEffect(() => {
    const api = window.capturePointDesktop;
    // PAIGE: a host with no speed test of this computer (`local-performance`) never offers one.
    if (!api || !hostHas('local-performance')) return;
    let live = true;
    const readFinished = () =>
      void Promise.resolve()
        .then(() => api.getSpeedCheckDetails())
        .then((details) => {
          if (live)
            setFacts((current) => ({
              ...current,
              finished:
                current.finished === true ||
                details.some((detail) => detail.depth === 'performance'),
            }));
        })
        .catch(() => {
          /* Unknown: the hint waits. */
        });
    let stop = () => {};
    try {
      stop = api.onSpeedCheckStateChanged((state) => {
        if (!live) return;
        setFacts((current) => ({
          finished:
            current.finished === true || state.kind === 'finished' ? true : current.finished,
          running: state.kind === 'running',
        }));
        if (state.kind !== 'running') readFinished();
      });
    } catch {
      /* No live updates; the reads below still answer. */
    }
    void Promise.resolve()
      .then(() => api.getSpeedCheckState())
      .then((state) => {
        if (live) setFacts((current) => ({ ...current, running: state.kind === 'running' }));
      })
      .catch(() => {
        /* Read as not running. */
      });
    readFinished();
    return () => {
      live = false;
      stop();
    };
  }, []);
  return facts.finished === null ? null : { finished: facts.finished, running: facts.running };
}

const ICONS: Record<HintId, ReactNode> = {
  'first-review': <CheckCircle2 size={19} aria-hidden="true" className="hint-icon waiting" />,
  'computer-check': <Gauge size={19} aria-hidden="true" className="hint-icon" />,
  assisted: <Sparkles size={19} aria-hidden="true" className="hint-icon" />,
  'let-it-run': <Plane size={19} aria-hidden="true" className="hint-icon ready" />,
};

/**
 * One hint, under the export line, or nothing. Never during a tour or while the tour is offered,
 * never on a sample job, and nothing until main's milestones have arrived.
 */
export function DocumentHint({
  jobId,
  documents,
  pilot,
  sample,
  reading = false,
  calibrating = false,
}: {
  jobId: string;
  documents: readonly ProcessResult['documents'][number][];
  pilot?: ProcessingMode | null;
  sample: boolean;
  reading?: boolean;
  /** The job is in Calibration: no hint offers Autopilot meanwhile (owner, 2026-10-31). */
  calibrating?: boolean;
}) {
  const tour = useTour();
  const settings = useHintSettings();
  const milestones = useMilestoneFacts();
  const assistedAvailable = useAiReviewAvailable();
  const reviewFacts = useJobReviewFacts(jobId, settings.enabled);
  const speedTest = useSpeedTestFacts();
  // The Needs review hint has done its job once a document that was waiting is confirmed here, on
  // their own job and outside a tour: it retires itself until Show hints is checked again.
  const waitingBefore = useRef<{ jobId: string; ids: Set<string> } | null>(null);
  useEffect(() => {
    const before = waitingBefore.current?.jobId === jobId ? waitingBefore.current.ids : null;
    waitingBefore.current = { jobId, ids: waitingDocumentIds(documents) };
    if (
      before &&
      firstReviewDone(before, documents, { sample, touring: Boolean(tour) }) &&
      closeHint('first-review')
    )
      hintTelemetry.closed('first-review', 'rule', jobId);
  }, [jobId, documents, sample, tour]);
  // The speed test hint retires for good once a test has finished here (unless its X closed it first).
  const closedByX = settings.closed.includes('computer-check');
  useEffect(() => {
    if (speedTest?.finished) hintTelemetry.speedTestFinished(closedByX, jobId);
  }, [speedTest?.finished]); // eslint-disable-line react-hooks/exhaustive-deps
  const waiting = documents.filter(
    (document) => document.needsReview && !document.confirmedAsWhole,
  ).length;
  const facts = {
    waiting,
    touring: Boolean(tour),
    offering: shouldOfferOwnTour({
      tour,
      sample,
      reading,
      documents: documents.length,
      answered: readTourFlag(TOUR_OFFER_KEY),
      taken: readTourFlag(TOUR_TAKEN_KEY),
      exportedBefore: milestones.exported,
    }),
    sample,
    assistedAvailable,
    // Two pilot modes: the retired straight-through value is Autopilot.
    pilot: pilotMode(pilot),
    calibrating,
    sendsByItself: reviewFacts.sendsByItself,
    review: reviewFacts.review,
    speedTest,
    enabled: settings.enabled,
    closed: settings.closed,
    letItRun: LET_IT_RUN
      ? { headline: LET_IT_RUN.headline, description: LET_IT_RUN.description }
      : undefined,
    secondLook: SECOND_LOOK
      ? { headline: SECOND_LOOK.headline, description: SECOND_LOOK.description }
      : undefined,
  };
  const id = milestones.known && !reading ? hintToShow(facts) : null;
  // Each hint shown is reported once per session.
  useEffect(() => {
    if (id) hintTelemetry.shown(id, jobId);
  }, [id, jobId]);
  if (!id) return null;
  const words = HINT_WORDS[id];
  return (
    <section className="hint-card" aria-label={`Hint: ${words.title(facts)}`}>
      <header>
        {ICONS[id]}
        <b className="hint-title">{words.title(facts)}</b>
        <button
          type="button"
          className="hint-close"
          data-telemetry="hint-close"
          aria-label="Close this hint"
          onClick={() => {
            hintTelemetry.closed(id, 'x', jobId);
            closeHint(id);
          }}
        >
          <X size={15} aria-hidden="true" />
        </button>
      </header>
      <p className="hint-text">{bold(words.text(facts))}</p>
      {words.action === 'speed-test' && (
        <div className="hint-actions">
          <button
            type="button"
            className="secondary-button small"
            data-telemetry="hint-speed-test"
            onClick={() => {
              hintTelemetry.action(id, 'speed-test', jobId);
              window.dispatchEvent(new Event(RUN_SPEED_TEST_EVENT));
            }}
          >
            <Gauge size={14} aria-hidden="true" />
            Run speed test
          </button>
        </div>
      )}
      {words.action === 'pilot-mode' && (
        <div className="hint-actions">
          <button
            type="button"
            className="secondary-button small"
            data-telemetry="hint-pilot-mode"
            onClick={() => {
              hintTelemetry.action(id, 'pilot-mode', jobId);
              window.dispatchEvent(
                new CustomEvent(OPEN_SETUP_SECTION_EVENT, { detail: 'thresholds' }),
              );
            }}
          >
            Open Pilot mode
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Settings › Appearance: the Show hints checkbox, with Take the tour beside it (owner, October 4);
 * Watch the tour again once taken. One checkbox (Brandy, October 5), never checked while hints are
 * off: it clears by itself once every hint this computer can show has been closed or retired, and
 * checking it brings them all back (showHintsChecked, setShowHints); the speed test hint only
 * while no test has finished on this computer.
 */
export function HintSettings() {
  const settings = useHintSettings();
  const assistedAvailable = useAiReviewAvailable();
  const speedTest = useSpeedTestFacts();
  return (
    <div className="hint-settings">
      <label className="hint-settings-control">
        <input
          type="checkbox"
          role="switch"
          data-telemetry="show-hints"
          checked={showHintsChecked(settings, assistedAvailable, speedTest)}
          onChange={(event) => {
            setShowHints(event.target.checked);
            hintTelemetry.showHints(event.target.checked, 'settings');
          }}
        />
        <span className="interface-size-setting-copy">
          <strong>Show hints</strong>
          <small>One hint at a time above your documents, when it can help.</small>
        </span>
      </label>
      <button
        type="button"
        className="secondary-button small hint-settings-tour"
        data-telemetry="tour-settings"
        onClick={() => window.dispatchEvent(new Event(TAKE_TOUR_EVENT))}
      >
        <Play size={13} aria-hidden="true" />
        {tourOfferLabel('Take the tour')}
      </button>
    </div>
  );
}
